import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Paper, TextField, Chip, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  IconButton, ToggleButton, ToggleButtonGroup, LinearProgress,
  FormControl, FormLabel, Checkbox, FormControlLabel, FormGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import VideocamIcon from '@mui/icons-material/Videocam';
import TimerIcon from '@mui/icons-material/Timer';
import SettingsIcon from '@mui/icons-material/Settings';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';

const SHOT_TYPES = ['Wide', 'Medium', 'Close-up', 'Extreme Close-up', 'Over-the-shoulder', 'POV', 'Insert', 'Establishing', 'Two-shot', 'Tracking'];
const MOVEMENTS = ['Static', 'Pan', 'Tilt', 'Dolly', 'Tracking', 'Handheld', 'Crane', 'Steadicam', 'Slider'];

const EQUIPMENT_OPTIONS = [
  { key: 'crane', label: 'Crane / Jib' },
  { key: 'steadicam', label: 'Steadicam / Gimbal' },
  { key: 'slider', label: 'Slider' },
  { key: 'drone', label: 'Drone' },
];

const STYLE_OPTIONS = [
  { key: 'cinematic', label: 'Cinematic', desc: 'Filmische composities, veel variatie' },
  { key: 'documentary', label: 'Documentaire', desc: 'Observerend, handheld, naturel' },
  { key: 'corporate', label: 'Corporate', desc: 'Strak, professioneel, clean' },
  { key: 'social_media', label: 'Social Media', desc: 'Kort, dynamisch, snelle cuts' },
];

// Setup questionnaire shown before first generation
const SetupConfig = ({ initialConfig, onGenerate, generating }) => {
  const [equipment, setEquipment] = useState(initialConfig?.equipment || []);
  const [style, setStyle] = useState(initialConfig?.style || 'corporate');
  const [complexity, setComplexity] = useState(initialConfig?.complexity || 'balanced');

  const toggleEquipment = (key) => {
    setEquipment(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleGenerate = () => {
    onGenerate({ equipment, style, complexity });
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SettingsIcon sx={{ color: '#7c3aed' }} />
        <Typography variant="h6" fontWeight={600}>Shotlist configuratie</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configureer de shoot-instellingen zodat de AI een passende shotlist genereert.
      </Typography>

      {/* Equipment */}
      <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
        <FormLabel sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
          Beschikbaar equipment
        </FormLabel>
        <FormGroup row>
          {EQUIPMENT_OPTIONS.map(eq => (
            <FormControlLabel key={eq.key}
              control={<Checkbox checked={equipment.includes(eq.key)} onChange={() => toggleEquipment(eq.key)}
                sx={{ '&.Mui-checked': { color: '#7c3aed' } }} />}
              label={eq.label}
            />
          ))}
        </FormGroup>
        <Typography variant="caption" color="text.secondary">
          {equipment.length === 0 ? 'Geen speciaal equipment → alleen standaard camera-opstellingen' : `${equipment.length} item(s) geselecteerd`}
        </Typography>
      </FormControl>

      {/* Style */}
      <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
        <FormLabel sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
          Shoot stijl
        </FormLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 1 }}>
          {STYLE_OPTIONS.map(s => (
            <Paper key={s.key} variant="outlined"
              onClick={() => setStyle(s.key)}
              sx={{
                p: 1.5, cursor: 'pointer', textAlign: 'center',
                borderColor: style === s.key ? '#7c3aed' : 'divider',
                bgcolor: style === s.key ? 'rgba(124,58,237,0.05)' : 'transparent',
                '&:hover': { borderColor: '#7c3aed' }
              }}>
              <Typography fontWeight={600} fontSize={14}>{s.label}</Typography>
              <Typography variant="caption" color="text.secondary">{s.desc}</Typography>
            </Paper>
          ))}
        </Box>
      </FormControl>

      {/* Complexity */}
      <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
        <FormLabel sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
          Uitgebreidheid shotlist
        </FormLabel>
        <ToggleButtonGroup value={complexity} exclusive onChange={(_, v) => v && setComplexity(v)} size="small">
          <ToggleButton value="minimal" sx={{ textTransform: 'none' }}>Minimaal (2-3 shots/scene)</ToggleButton>
          <ToggleButton value="balanced" sx={{ textTransform: 'none' }}>Gebalanceerd</ToggleButton>
          <ToggleButton value="extensive" sx={{ textTransform: 'none' }}>Uitgebreid (extra coverages)</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" size="large"
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
          onClick={handleGenerate} disabled={generating}
          sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
          {generating ? 'AI genereert shotlist...' : 'Genereer shotlist'}
        </Button>
      </Box>

      {generating && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#7c3aed' } }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
            Dit kan 30-60 seconden duren...
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const ShotlistView = ({ project, onRefresh, onNext, onBack }) => {
  const [scenes, setScenes] = useState(project.scenes || []);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [pdfBezig, setPdfBezig] = useState(false);

  const downloadPdf = async () => {
    setPdfBezig(true);
    try {
      // Eerst de zichtbare stand opslaan — anders exporteer je de databaseversie
      // terwijl het scherm al aangepast is (Codex ronde 12 #6).
      const allShots = scenes.flatMap(sc => (sc.shots || []).map(shot => ({ ...shot })));
      if (allShots.length) {
        const opslag = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}/shots`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(allShots),
        });
        if (!opslag.ok) throw new Error('Opslaan vóór PDF mislukt');
      }
      const resp = await authFetch(`${BACKEND_URL}/Api/script-breakdown/${project.id}/shotlist-pdf`);
      if (!resp.ok) throw new Error('PDF genereren mislukt');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Shotlist - ${project.title}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setPdfBezig(false); }
  };
  const autoGenerated = useRef(false);

  // Sync scenes when project data refreshes
  useEffect(() => {
    setScenes(project.scenes || []);
  }, [project]);

  const totalShots = scenes.reduce((sum, s) => sum + (s.shots?.length || 0), 0);
  const totalSetupMinutes = scenes.reduce((sum, s) =>
    sum + (s.shots || []).reduce((ss, shot) => ss + (shot.setup_time_minutes || 0), 0), 0
  );

  // Auto-show config when arriving with no shots
  useEffect(() => {
    if (totalShots === 0 && !autoGenerated.current && scenes.length > 0) {
      autoGenerated.current = true;
      setShowConfig(true);
    }
    // eslint-disable-next-line
  }, []);

  const doGenerate = async (setupConfig) => {
    setGenerating(true);
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}/generate-shotlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setup_config: setupConfig }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || 'Generatie mislukt');
      }
      const data = await resp.json();
      toast.success(`${data.shots_created} shots gegenereerd`);
      setShowConfig(false);
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const updateShot = (sceneIdx, shotIdx, field, value) => {
    const updated = [...scenes];
    const shots = [...(updated[sceneIdx].shots || [])];
    shots[shotIdx] = { ...shots[shotIdx], [field]: value };
    updated[sceneIdx] = { ...updated[sceneIdx], shots };
    setScenes(updated);
  };

  const handleRegenerate = async () => {
    if (scenes.some(s => s.shots?.length > 0)) {
      if (!(await bevestig({ titel: 'Shotlist overschrijven?', tekst: 'De bestaande shotlist wordt vervangen door een nieuwe generatie.', knop: 'Overschrijven' }))) return;
    }
    setShowConfig(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const allShots = scenes.flatMap(s => (s.shots || []).map(shot => ({ ...shot })));
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}/shots`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(allShots),
      });
      if (!resp.ok) throw new Error('Opslaan mislukt');
      toast.success('Shotlist opgeslagen');
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const totalDuration = scenes.reduce((sum, s) =>
    sum + (s.shots || []).reduce((ss, shot) => ss + (shot.duration_seconds || 0), 0), 0
  );
  const hasShots = totalShots > 0;

  // Show config questionnaire
  if (showConfig) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>Shotlist</Typography>
        </Box>
        <SetupConfig
          initialConfig={project.setup_config}
          onGenerate={doGenerate}
          generating={generating}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button startIcon={<NavigateBeforeIcon />} onClick={onBack}>Terug</Button>
          {hasShots && (
            <Button onClick={() => setShowConfig(false)}>Annuleren</Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Shotlist
          </Typography>
          {hasShots && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {totalShots} shots | Beeldduur: {Math.floor(totalDuration / 60)}:{(totalDuration % 60).toString().padStart(2, '0')}
              </Typography>
              {totalSetupMinutes > 0 && (
                <Chip icon={<TimerIcon sx={{ fontSize: 14 }} />} size="small" variant="outlined"
                  label={`Productietijd: ~${Math.floor(totalSetupMinutes / 60)}u${(totalSetupMinutes % 60).toString().padStart(2, '0')}`}
                  sx={{ fontSize: 12 }} />
              )}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasShots && (
            <Button size="small" variant="outlined" onClick={downloadPdf} disabled={pdfBezig}
              startIcon={pdfBezig ? <CircularProgress size={14} /> : null} sx={{ textTransform: 'none' }}>
              📄 Shotlist-PDF
            </Button>
          )}
          <Button size="small" startIcon={<SettingsIcon />}
            onClick={handleRegenerate} disabled={generating}>
            {hasShots ? 'Opnieuw genereren' : 'Configureren'}
          </Button>
          {hasShots && (
            <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave} disabled={saving}>
              Opslaan
            </Button>
          )}
        </Box>
      </Box>

      {!hasShots && !showConfig && (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <Typography color="text.secondary" gutterBottom>
            Nog geen shots. Klik op "Configureren" om de shotlist in te stellen.
          </Typography>
        </Paper>
      )}

      {scenes.filter(s => s.shots?.length > 0).map((scene, sceneIdx) => (
        <Accordion key={scene.id} defaultExpanded sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Chip label={`Scene ${scene.scene_number}`} size="small" color="primary" />
              <Typography fontWeight={600} sx={{ flexGrow: 1 }}>
                {scene.heading || scene.location || `Scene ${scene.scene_number}`}
              </Typography>
              <Chip label={`${scene.shots?.length || 0} shots`} size="small" variant="outlined" />
              {(() => {
                const sceneSetup = (scene.shots || []).reduce((s, sh) => s + (sh.setup_time_minutes || 0), 0);
                return sceneSetup > 0 ? (
                  <Chip icon={<TimerIcon sx={{ fontSize: 12 }} />} label={`~${sceneSetup} min`}
                    size="small" variant="outlined" sx={{ fontSize: 11 }} />
                ) : null;
              })()}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <TableCell sx={{ fontWeight: 600, width: 55 }}>Shot</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 120 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 110 }}>Beweging</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Beschrijving</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 55 }}>Sec</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 65 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TimerIcon sx={{ fontSize: 14 }} /> Min
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(scene.shots || []).map((shot, shotIdx) => (
                    <TableRow key={shot.id || shotIdx} hover>
                      <TableCell>
                        <TextField size="small" variant="standard" value={shot.shot_number || ''}
                          onChange={(e) => updateShot(sceneIdx, shotIdx, 'shot_number', e.target.value)}
                          sx={{ width: 45 }} />
                      </TableCell>
                      <TableCell>
                        <Select size="small" variant="standard" value={shot.shot_type || ''}
                          onChange={(e) => updateShot(sceneIdx, shotIdx, 'shot_type', e.target.value)}
                          sx={{ fontSize: 13, minWidth: 90 }}>
                          {SHOT_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select size="small" variant="standard" value={shot.movement || ''}
                          onChange={(e) => updateShot(sceneIdx, shotIdx, 'movement', e.target.value)}
                          sx={{ fontSize: 13, minWidth: 80 }}>
                          {MOVEMENTS.map(m => <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m}</MenuItem>)}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" variant="standard" fullWidth multiline maxRows={3}
                          value={shot.description || ''}
                          onChange={(e) => updateShot(sceneIdx, shotIdx, 'description', e.target.value)}
                          sx={{ '& .MuiInput-input': { fontSize: 13 } }} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" variant="standard" type="number"
                          value={shot.duration_seconds || ''}
                          onChange={(e) => updateShot(sceneIdx, shotIdx, 'duration_seconds', parseInt(e.target.value) || 0)}
                          sx={{ width: 45 }} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" variant="standard" type="number"
                          value={shot.setup_time_minutes || ''}
                          onChange={(e) => updateShot(sceneIdx, shotIdx, 'setup_time_minutes', parseInt(e.target.value) || 0)}
                          sx={{ width: 50 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button startIcon={<NavigateBeforeIcon />} onClick={onBack}>Terug</Button>
        {hasShots && (
          <Button variant="contained" endIcon={<NavigateNextIcon />} onClick={async () => { await handleSave(); onNext(); }}
            sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
            Verder naar planning
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ShotlistView;
